/**
 * Done summary (S05) — confirmation card with the trial state, summary grid,
 * and the forward action into the workspace.
 *
 * Pulls phone / company / trial values from the Zustand slice. Once the
 * identity vertical lands, replace the slice reads with `trpc.identity.me`
 * so the screen survives F5.
 *
 * @see prototype/onboarding/done.html — S05 baseline
 */
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { differenceInCalendarDays, format, parseISO } from "date-fns";
import { Check } from "lucide-react";
import { Card, CardBody, Button } from "@flint/ui";
import { meQueryOptions } from "../../../lib/session.ts";
import { useOnboardingFlow } from "../lib/flow-state.ts";

export function DoneSummary() {
  const me = useQuery(meQueryOptions);
  const flowPhone = useOnboardingFlow((s) => s.phone);
  const flowTrialEndsOn = useOnboardingFlow((s) => s.trialEndsOn);
  const reset = useOnboardingFlow((s) => s.reset);

  const phone = flowPhone ?? me.data?.user.phone ?? null;
  const trialEndsOn = flowTrialEndsOn ?? me.data?.company?.subscription?.trialEndsOn ?? null;
  const trialDaysLeft = me.data?.company?.subscription?.trialDaysLeft ?? null;
  const phoneDisplay = phone ? `+91 ${phone.slice(0, 5)} ${phone.slice(5)}` : "Not provided";
  const trialEndDate = trialEndsOn ? parseISO(trialEndsOn) : null;
  const daysLeft =
    trialDaysLeft ??
    (trialEndDate ? Math.max(0, differenceInCalendarDays(trialEndDate, new Date())) : 14);
  const trialFormatted = trialEndDate ? format(trialEndDate, "d MMM yyyy") : null;

  return (
    <Card>
      <CardBody className="flex flex-col gap-4">
        <span
          aria-hidden
          className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-ok-100 text-ok ring-1 ring-line"
        >
          <Check size={22} strokeWidth={2.2} />
        </span>

        <div className="flex justify-between items-center gap-3 flex-wrap p-3 rounded bg-sky-50 ring-1 ring-sky-100/60">
          <div>
            <strong className="block text-xs leading-[16px] font-semibold text-ink">
              14-day Growth trial is active
            </strong>
            <span className="block mt-0.5 text-xs leading-[17px] text-ink-2">
              {trialFormatted
                ? `Trial ends on ${trialFormatted}. Billing can be activated later from the workspace.`
                : "Trial running. Billing can be activated later from the workspace."}
            </span>
          </div>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-paper text-sky-600 text-xs font-semibold ring-1 ring-line whitespace-nowrap">
            <span className="font-mono tabular-nums">{daysLeft}</span> days left
          </span>
        </div>

        <div className="flex flex-col gap-3 p-3.5 rounded bg-bg ring-1 ring-line/80">
          <div className="flex justify-between items-start gap-3">
            <div>
              <strong className="block text-sm leading-[18px] font-semibold text-ink">
                Workspace ready
              </strong>
              <span className="block mt-0.5 text-xs leading-[15px] text-muted">
                Created and ready for founder use.
              </span>
            </div>
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-ok-100 text-ok text-xs font-medium whitespace-nowrap">
              <span aria-hidden className="w-1 h-1 rounded-full bg-ok" />
              Trial live
            </span>
          </div>

          <div className="grid grid-cols-2 gap-2.5">
            <SummaryItem
              label="Phone"
              value={<span className="font-mono tabular-nums">{phoneDisplay}</span>}
            />
            <SummaryItem label="GSTIN" value="Optional · add later" />
            <SummaryItem label="Migration" value="Deferred for later" />
            <SummaryItem label="Billing" value="Activation due before trial end" />
          </div>
        </div>

        <p className="text-xs leading-[17px] text-muted">
          The next surface after this handoff is your workspace — every module in one place, with
          trial status, billing activation, and the first operating workflows a click away.
        </p>

        <div className="flex gap-2.5 flex-wrap">
          <Button asChild>
            <Link to="/launch" onClick={() => reset()}>
              Open your workspace
            </Link>
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}

function SummaryItem({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="pt-2.5 border-t border-line">
      <span className="block text-xs uppercase tracking-[0.14em] text-muted mb-1">{label}</span>
      <span className="block text-sm leading-[18px] font-medium text-ink">{value}</span>
    </div>
  );
}
